import type { HttpErrorResponse } from '@angular/common/http';

import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Subscription } from 'rxjs';
import {
  IPayer,
  type IPayerAPI,
} from 'src/app/shared/interfaces/payer/payers.interface';
import { AlertService } from 'src/app/shared/services/alert.service';
import { DatepickerService } from 'src/app/shared/services/datepicker.service';
import { PayerGQLService } from 'src/app/shared/services/payers/payers.graphql.service';
import { PayerService } from 'src/app/shared/services/payers/payers.service';
import { DEFAULT_MODAL_OPTIONS } from 'src/app/shared/variables/variables';

import { EditInsuranceSetupModalComponent } from '../../insurance/edit-insurance-setup-modal/edit-insurance-setup-modal.component';

@Component({
  selector: 'app-edit-payer-modal',
  templateUrl: './edit-payer-modal.component.html',
})
export class EditPayerModalComponent implements OnInit, OnDestroy {
  @Input() payer?: IPayer;
  @Input() payerId: string;
  @Input() isReadOnly = false;

  payerForm: UntypedFormGroup;
  isEditMode = false;
  isLoading = true;
  isPayerRecordActive: boolean;

  sourceOutputs = [
    {
      value: 'BB',
      description:
        'Use Billing in both the Receiver and Service Provider of a request',
    },
    {
      value: 'BR',
      description:
        'Use Billing in the Receiver and Rendering in the Service Provider of a request',
    },
    {
      value: 'RB',
      description:
        'Use Rendering in the Receiver and Billing in the Service Provider of a request',
    },
    {
      value: 'RR',
      description:
        'Use Rendering in both the Receiver and Service Provider of a request',
    },
  ];

  private subscriptions: Subscription = new Subscription();
  private dateTimeString: string = Date.now().toString();

  constructor(
    private activeModal: NgbActiveModal,
    private fb: UntypedFormBuilder,
    private modal: NgbModal,
    private alertService: AlertService,
    private payerService: PayerService,
    private datepickerService: DatepickerService,
    private payerGQLService: PayerGQLService,
  ) {}

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  ngOnInit() {
    this.isEditMode = !!this.payer || !!this.payerId;

    if (this.payer) {
      this.isPayerRecordActive = this.payer.recordStatus === 'A' ? true : false;
    }

    this.payerForm = this.fb.group({
      id: [{ value: this.payer?.id ?? '', disabled: this.isEditMode }],
      recordStatus: [this.payer?.recordStatus ?? 'A'],
      name: [this.payer?.name ?? ''],
      contact: this.fb.group({
        name: [this.payer?.contactName ?? ''],
        phone: [this.payer?.contactPhone ?? ''],
        fax: [this.payer?.contactFax ?? ''],
      }),
      dateLastDbUpdate: [
        {
          value: this.payer?.dateLastDbUpdate ?? this.dateTimeString,
          disabled: true,
        },
      ],
      processorId: [this.payer?.processorId ?? ''],
      ubProcessorId: [this.payer?.ubProcessorId ?? ''],
      neicPayerType: [this.payer?.neicPayerType ?? ''],
      neicId: [this.payer?.neicId ?? '', [Validators.required]],
      notes: [this.payer?.notes ?? ''],
      requiresEnrollment: [this.payer?.requiresEnrollment ?? ''],
      payorState: [this.payer?.payorState],
      secondaryClaims: [this.payer?.secondaryClaims ?? 'N'],
      realTimeId: [this.payer?.realTimeId ?? ''],
      followUpNumberOfDays: [this.payer?.followUpNumberOfDays],
      billingIndicator: [this.payer?.billingIndicator ?? ''],
      eraType: [this.payer?.eraType ?? ''],
      eligibilityEnrollment: [this.payer?.eligibilityEnrollment ?? ''],
      legacy: [this.payer?.legacy ?? ''],
      claimStatusId: [this.payer?.claimStatusId ?? ''],
      claimFilingIndicator: [this.payer?.claimFilingIndicator ?? ''],
      cardId: [this.payer?.cardId ?? ''],
      batchClaimStatus: [this.payer?.batchClaimStatus ?? ''],
      institutionalEnrollment: [this.payer?.institutionalEnrollment ?? ''],
      claimStatusEnrollment: [this.payer?.claimStatusEnrollment ?? ''],
      attachmentEnrollment: [this.payer?.attachmentEnrollment ?? ''],
      eraEnrollment: [this.payer?.eraEnrollment ?? ''],
      claimStatusType: [this.payer?.claimStatusType ?? ''],
      eligibilityType: [this.payer?.eligibilityType ?? ''],
      institutionalType: [this.payer?.institutionalType ?? ''],
      attachmentType: [this.payer?.attachmentType ?? ''],
      dateSetup: [this.payer?.dateSetup],
      dateTerminate: [this.payer?.dateTerminate],
      attachmentsProcessorId: [this.payer?.attachmentsProcessorId ?? ''],
    });

    if (this.payerId) {
      this.getPayer(this.payerId);
    } else {
      this.isLoading = false;
    }

    if (this.isReadOnly) {
      this.payerForm.disable();
    }
  }

  close() {
    this.activeModal.dismiss();
  }

  save() {
    this.isLoading = true;

    const isClaimsStatus = this.payerForm.getRawValue().claimStatusId
      ? 'Y'
      : 'N';

    const formValue = {
      ...this.payerForm.getRawValue(),
      claimStatus: isClaimsStatus,
      followUpNumberOfDays:
        this.payerForm.getRawValue()?.followUpNumberOfDays || 0,
      dateLastDbUpdate:
        this.payer?.dateLastDbUpdate ??
        this.datepickerService.toISOStringInUTC(this.dateTimeString),
      dateSetup:
        this.payer?.dateSetup ??
        this.datepickerService.toISOStringInUTC(this.dateTimeString),
      dateTerminate:
        this.payer?.dateTerminate ??
        this.datepickerService.toISOStringInUTC(this.dateTimeString),
      recordStatus: this.payer?.recordStatus,
      publishEra: this.payer?.publishEra,
      publishInstitutional: this.payer?.publishInstitutional,
      publishProfessional: this.payer?.publishProfessional,
    };

    const request$ = this.isEditMode
      ? this.payerService.editPayer(formValue as IPayerAPI)
      : this.payerService.createPayer(formValue as IPayerAPI);

    this.subscriptions.add(
      request$.subscribe({
        next: () => {
          const successMessage = this.isEditMode
            ? 'Payer was updated successfully!'
            : 'Payer was created successfully!';
          this.alertService.success(successMessage);
          this.isLoading = false;
        },
        error: (error: unknown) => {
          if (error) {
            this.alertService.error(error as HttpErrorResponse | string);
          }
          this.isLoading = false;
        },
      }),
    );
  }

  addInsurance() {
    const modal = this.modal.open(EditInsuranceSetupModalComponent, {
      ...DEFAULT_MODAL_OPTIONS,
      size: 'md',
    });

    const mi = modal.componentInstance as EditInsuranceSetupModalComponent;

    mi.payer = {
      ...this.payer,
      ...this.payerForm.value,
    };
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onRecordStatusChange(checkbox: any): void {
    if (checkbox.currentTarget.checked) {
      this.isPayerRecordActive = true;
    } else {
      this.isPayerRecordActive = false;
    }
  }

  private getPayer(payerId: string) {
    this.payerGQLService.getPayer(payerId).subscribe({
      next: (response) => {
        const [payer] = response.nodes;
        this.payer = payer;

        this.payerForm.patchValue({
          ...this.payer,
          contact: {
            name: this.payer.contactName,
            phone: this.payer.contactPhone,
            fax: this.payer.contactFax,
          },
          // publishEra:               [ this.payer?.publishEra === 'Y' ? true : false],
          // publishInstitutional:     [ this.payer?.publishInstitutional === 'Y' ? true : false],
          // publishProfessional:      [ this.payer?.publishProfessional === 'Y' ? true : false],
        });

        this.isPayerRecordActive =
          this.payer.recordStatus === 'A' ? true : false;

        this.isLoading = false;
      },
      error: (error: unknown) => {
        if (error) {
          this.alertService.error(error as HttpErrorResponse | string);
        }
        this.isLoading = false;
      },
    });
  }
}
